import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, Box, Text } from '@bonusly/design-system';

import { AdminLink } from './AdminLink';
import { NavSection } from './NavSection';
import { NavConfig } from './types/nav';

interface NavProps {
	nav: NavConfig;
}

export const Nav: FC<NavProps> = ({ nav }) => {
	const { t } = useTranslation('vertical_navbar');
	const [openSections, setOpenSections] = useState<number[]>([]);
	const internalNavigationRef = useRef(false);

	// Update open sections when nav.activeSectionIndex changes (URL changes).
	// activeSectionIndex is undefined when the current route maps to no accordion
	// group — e.g. a direct-link section like /super_admin/kvms. That case must
	// still run so a previously-expanded group collapses and the internal-nav flag
	// is cleared (rather than skipping the effect and leaving stale state).
	useEffect(() => {
		const activeSectionIndex = nav.activeSectionIndex;
		const wasInternalNavigation = internalNavigationRef.current;
		internalNavigationRef.current = false;

		setOpenSections((prev) => {
			// Internal navigation (clicking a nav link) to an accordion section: just
			// ensure that section is open, without collapsing the others.
			if (wasInternalNavigation && activeSectionIndex !== undefined) {
				return prev.includes(activeSectionIndex) ? prev : [...prev, activeSectionIndex];
			}

			// Otherwise — external navigation, or navigation to a direct-link section
			// (no accordion index) — collapse any open group that doesn't contain the
			// current path. Open/close indices are in Chakra Accordion space, which
			// counts only expandable sections; direct-link sections (with `url`) are
			// chevron-less links and are excluded so indices stay aligned. A
			// direct-link route matches no group, so every group collapses.
			const accordionSections = nav.sections.filter((section) => !section.url);
			const currentPath = window.location.pathname;
			const sectionContainsCurrentPath = (sectionIndex: number) => {
				const section = accordionSections[sectionIndex];
				if (!section) return false;
				return Boolean(
					section.links?.some((link) => {
						// Extract path from URL if needed
						let linkPath = link.url;
						if (link.url.includes('http')) {
							try {
								linkPath = new URL(link.url).pathname;
							} catch (e) {
								linkPath = link.url;
							}
						}

						// Check for matches
						if (linkPath === currentPath) return true;
						if (link.matchActive && link.matchActive.test(currentPath)) return true;
						return false;
					})
				);
			};

			// Keep the active section (if any) and any group matching the current path.
			const newOpenSections = prev.filter((index) => index === activeSectionIndex || sectionContainsCurrentPath(index));
			if (activeSectionIndex !== undefined && !newOpenSections.includes(activeSectionIndex)) {
				newOpenSections.push(activeSectionIndex);
			}
			return newOpenSections;
		});
	}, [nav.activeSectionIndex, nav.sections]);

	// Handle accordion changes from user interaction
	const handleAccordionChange = (expandedIndex: number | number[] | undefined) => {
		if (Array.isArray(expandedIndex)) {
			setOpenSections(expandedIndex);
		} else if (typeof expandedIndex === 'number') {
			setOpenSections([expandedIndex]);
		} else {
			setOpenSections([]);
		}
	};

	// Mark navigation as internal when nav links are clicked
	const handleNavLinkClick = () => {
		internalNavigationRef.current = true;
	};

	if (!nav.sections.length) {
		return null;
	}

	return (
		<Box
			data-testid={nav.id}
			borderBottomColor="border.default"
			borderBottomWidth={1}
			pt={3}
			pb={1}
			_last={{
				borderBottomColor: 'transparent',
			}}
		>
			<Text color="text.softest" fontWeight="medium" fontSize={1} pl={3} pb={2}>
				{t(nav.title as any)}
			</Text>

			{nav.links?.map((link) => {
				return (
					<AdminLink key={link.url} icon={link.icon ? <link.icon /> : <></>} to={link.url} mb={2} onClick={handleNavLinkClick}>
						{t(link.label as any)}
					</AdminLink>
				);
			})}

			<Accordion allowMultiple as="ul" aria-label={t(nav.label as any)} role="menu" borderRadius="sm" index={openSections} onChange={handleAccordionChange}>
				{nav.sections.map((section, index) => {
					return <NavSection key={index} section={section} onNavLinkClick={handleNavLinkClick} />;
				})}
			</Accordion>
		</Box>
	);
};
